<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import numberMixin from '../../../../mixins/numberMixin';
import i18n from '../../../../i18n';

export default {
  name: 'SkiRentalOverview',
  data() {
    return {
      isPriceChanged: false,
      isProductChanged: false,
      editPersonInformationDialog: false,
      editProductDialog: false,
      selectedRental: undefined,
      selectedRentalProduct: undefined,
      isSearchingForAnotherProduct: false,
      selectedArea: undefined,
      supplierRefs: {},
    };
  },
  mixins: [numberMixin],
  filters: {
    formatDate(date) {
      if (!date) {
        return null;
      }
      const [year, month, day] = date.split('-');
      return `${day}-${month}-${year}`;
    },
  },
  created() {
    this.rentals.forEach((rental) => {
      rental.skiRentalProducts.forEach((rentalProduct) => {
        this.$set(this.supplierRefs, rentalProduct.id, rentalProduct.supplierRef);
      });
    });
  },
  methods: {
    ...mapActions({
      saveRentalProduct: 'skirental/rentalProductUpdate',
      saveRentalProductPrice: 'skirental/rentalProductPriceUpdate',
      saveRentalProductSupplierRef: 'skirental/rentalProductSupplierRefUpdate',
      saveRentalParticipant: 'skirental/rentalParticipantUpdate',
      saveCancellationFeeNet: 'skirental/rentalCancellationFeeNetUpdate',
      deleteRentalProduct: 'skirental/rentalProductDelete',
      stopRentalProduct: 'skirental/rentalProductStop',
      cancelRentalProduct: 'skirental/rentalProductCancel',
      reOpenRentalProduct: 'skirental/rentalProductChangeStatus',
      loadAvailableRentalProducts: 'skirental/availableRentalProductsLoad',
      sendSkiRentalRequestEmail: 'skirental/sendSkiRentalRequestEmail',
      setChangeProductBookingParticipant: 'skirental/setChangeProductBookingParticipant',
      setChangeProductStartDate: 'skirental/setChangeProductStartDate',
      setChangeProductNumberOfDays: 'skirental/setChangeProductNumberOfDays',
      setChangeProductLevelType: 'skirental/setChangeProductLevelType',
      setChangeProductSkiType: 'skirental/setChangeProductSkiType',
      setChangeProductPersonCategory: 'skirental/setChangeProductPersonCategory',
      setChangeProductPackageType: 'skirental/setChangeProductPackageType',
      setBookingParticipant: 'skirental/setBookingParticipant',
      setStartDate: 'skirental/setStartDate',
      setNumberOfDays: 'skirental/setNumberOfDays',
      setLevelTypes: 'skirental/setLevelTypes',
      setSkiTypes: 'skirental/setSkiTypes',
      setArea: 'skirental/setArea',
      reOpen: 'skirental/reOpen',
      assignParticipant: 'skirental/assignParticipantToSkiPass',
    }),
    updateSupplierRef(skiRentalProduct) {
      const supplierRef = this.supplierRefs[skiRentalProduct.id] ? this.supplierRefs[skiRentalProduct.id] : null;
      if (supplierRef && supplierRef.length && supplierRef !== skiRentalProduct.supplierRef) {
        this.saveRentalProductSupplierRef({ supplierRef, skiRentalProduct });
      } else {
        this.$set(this.supplierRefs, skiRentalProduct.id, skiRentalProduct.supplierRef);
      }
    },
    selectCurrentPerson(rental) {
      this.selectedRental = Object.assign({}, rental);
    },
    deselectCurrentPerson() {
      this.selectedRental = undefined;
    },
    selectCurrentRentalProduct(rentalProduct) {
      this.selectedRentalProduct = Object.assign({}, rentalProduct);
    },
    deselectCurrentRentalProduct() {
      this.selectedRentalProduct = undefined;
    },
    openEditProductDialog(rental, rentalProduct) {
      this.selectCurrentPerson(rental);
      this.selectCurrentRentalProduct(rentalProduct);
      this.isPriceChanged = false;
      this.isProductChanged = false;
      this.isSearchingForAnotherProduct = false;
      this.setChangeProductBookingParticipant(rental.participant);
      this.setBookingParticipant(rental.participant);
      if (!this.selectedStartDate) {
        this.setChangeProductStartDate(this.defaultStartDate);
      }
      this.setStartDate(this.selectedStartDate || this.defaultStartDate);
      this.setNumberOfDays(rentalProduct.numberOfDays);
      this.setLevelTypes([]);
      this.setSkiTypes([]);
      this.setChangeProductPersonCategory(rentalProduct.productsSkiRentalPersonCategoryId);
      this.setChangeProductNumberOfDays(rentalProduct.numberOfDays);
      this.setChangeProductLevelType(rentalProduct.productsSkiRentalLevelId);
      this.setChangeProductSkiType(rentalProduct.productsSkiRentalSkiTypeId);
      this.setChangeProductPackageType(rentalProduct.productsSkiRentalPackageId);
      this.editProductDialog = true;
    },
    async saveProductDialog() {
      // eslint-disable-next-line no-alert
      if (!confirm(i18n.t('ski.rental.saveAreYouSure'))) {
        return;
      }
      let closeDiaLog = false;
      let price;
      let priceNet;
      if (this.isSearchingForAnotherProduct
        && this.isProductChanged
        && this.availableSkiRentalProductUsedForChangeProduct
      ) {
        const newProduct = this.availableSkiRentalProductUsedForChangeProduct;
        price = this.$options.filters.roundNumber(this.$options.filters.numberToDots(
          newProduct.priceGross,
        ));
        priceNet = this.$options.filters.roundNumber(this.$options.filters.numberToDots(
          newProduct.priceNet,
        ));
        let discount = null;
        let discountNet = null;
        let discountBookFrom = null;
        let discountBookTo = null;
        if (newProduct.discount && newProduct.discount.id) {
          discount = this.$options.filters.roundNumber(newProduct.discountGross);
          discountNet = this.$options.filters.roundNumber(newProduct.discountNet);
          discountBookFrom = newProduct.discount.bookFrom;
          discountBookTo = newProduct.discount.bookTo;
        }
        if (this.isPriceChanged) {
          price = this.$options.filters.roundNumber(this.$options.filters.numberToDots(
            this.selectedRentalProduct.totalGrossAfterDiscount,
          ));
          priceNet = this.$options.filters.roundNumber(this.$options.filters.numberToDots(
            this.selectedRentalProduct.totalNetAfterDiscount,
          ));
          if (discount && discountNet) {
            price += discount;
            priceNet += discountNet;
          }
        }
        const personCategoryAges = ' ('
          .concat(i18n.t('ski.rental.years-from-to', {
            from: newProduct.personCategory.ageFrom,
            to: newProduct.personCategory.ageTo,
          }))
          .concat(')');
        const changedProduct = {
          id: this.selectedRentalProduct.id,
          areaId: this.selectedRentalProduct.areaId,
          bookingSkiRentalId: this.selectedRental.id,
          priceByUserAgentId: this.isPriceChanged ? this.user.id : null,
          cancelledUserAgentId: null,
          productsSkiRentalSeasonId: newProduct.season.id,
          productsSkiRentalSkiTypeId: newProduct.skiType.id,
          productsSkiRentalPackageId: newProduct.package.id,
          productsSkiRentalPersonCategoryId: newProduct.personCategory.id,
          productsSkiRentalLevelId: newProduct.level.id,
          status: 'initiated',
          salesType: 'booking',
          supplierRef: null,
          firstSkiDay: this.selectedStartDate,
          numberOfDays: newProduct.priceDay.days,
          price: this.$options.filters.roundNumber(price),
          priceNet: this.$options.filters.roundNumber(priceNet),
          currencyNet: newProduct.season.supplier.currency,
          supplierName: newProduct.season.supplier.name,
          name: newProduct.name,
          seasonName: newProduct.season.name,
          skiTypeName: newProduct.skiType.name,
          packageName: newProduct.package.name,
          personCategoryName: newProduct.personCategory.name + personCategoryAges,
          levelName: newProduct.level.name,
          nameSupplier: newProduct.externalName,
          seasonNameSupplier: newProduct.season.name,
          skiTypeNameSupplier: newProduct.skiType.identifier,
          packageNameSupplier: newProduct.package.identifier,
          personCategoryNameSupplier: newProduct.personCategory.externalName
            + personCategoryAges,
          levelNameSupplier: newProduct.level.identifier,
          discount,
          discountNet,
          discountBookFrom,
          discountBookTo,
        };
        closeDiaLog = await this.saveRentalProduct({
          changedProduct,
          bookingId: this.booking.id,
          participantId: this.selectedRental.bookingParticipantId,
        });
      } else if (this.isPriceChanged) {
        price = this.selectedRentalProduct.totalGrossAfterDiscount;
        priceNet = this.selectedRentalProduct.totalNetAfterDiscount;
        price = this.$options.filters.roundNumber(this.$options.filters.numberToDots(price));
        priceNet = this.$options.filters.roundNumber(this.$options.filters.numberToDots(priceNet));
        if (this.selectedRentalProduct.discount && this.selectedRentalProduct.discountNet) {
          price += this.$options.filters.roundNumber(this.selectedRentalProduct.discount);
          priceNet += this.$options.filters.roundNumber(this.selectedRentalProduct.discountNet);
        }
        const changedProductPrice = {
          id: this.selectedRentalProduct.id,
          priceByUserAgentId: this.user.id,
          price,
          priceNet,
          currencyNet: this.selectedRentalProduct.currencyNet,
        };
        closeDiaLog = await this.saveRentalProductPrice({
          changedProductPrice,
          bookingId: this.booking.id,
        });
      }
      closeDiaLog = await this.saveCancellationFeeNet({
        skiRentalProductId: this.selectedRentalProduct.id,
        cancellationFeeNet: this.selectedRentalProduct.cancellationFeeNet !== null
          ? this.$options.filters.roundNumber(this.selectedRentalProduct.cancellationFeeNet)
          : 0,
        bookingId: this.booking.id,
      });
      if (closeDiaLog && !this.rentalsSaveError) {
        this.editProductDialog = false;
        this.deselectCurrentPerson();
      }
    },
    closeProductDialog() {
      this.editProductDialog = false;
      this.selectedArea = undefined;
      this.isSearchingForAnotherProduct = false;
      this.deselectCurrentPerson();
      this.deselectCurrentRentalProduct();
    },
    deleteProductDialog(skiRentalProductId) {
      // eslint-disable-next-line no-alert
      if (confirm(i18n.t('ski.rental.deleteAreYouSure'))) {
        this.deleteRentalProduct({
          skiRentalProductId,
          salesAgentUserId: this.user.id,
        });
      }
    },
    stopProductDialog(skiRentalProductId) {
      // eslint-disable-next-line no-alert
      if (confirm(i18n.t('ski.rental.stopAreYouSure'))) {
        this.stopRentalProduct({
          skiRentalProductId,
          salesAgentUserId: this.user.id,
          bookingId: this.booking.id,
        });
      }
    },
    cancelProductDialog(skiRentalProductId) {
      // eslint-disable-next-line no-alert
      if (confirm(i18n.t('ski.rental.cancelAreYouSure'))) {
        this.cancelRentalProduct({
          skiRentalProductId,
          salesAgentUserId: this.user.id,
          bookingId: this.booking.id,
        });
      }
    },
    reOpenProductDialog(skiRentalProductId) {
      // eslint-disable-next-line no-alert
      if (confirm(i18n.t('ski.rental.reopenAreYouSure'))) {
        this.reOpen({
          skiRentalProductId,
          bookingId: this.booking.id,
          status: 'initiated',
        });
      }
    },
    openEditPersonInformationDialog(rental, rentalProduct) {
      this.selectCurrentPerson(rental);
      this.selectCurrentRentalProduct(rentalProduct);
      this.editPersonInformationDialog = true;
    },
    async saveEditPersonInformationDialog() {
      if (await this.saveRentalParticipant({ skiRental: this.selectedRental })) {
        if (!this.rentalsSaveError) {
          this.editPersonInformationDialog = false;
        }
      }
    },
    closeEditPersonInformationDialog() {
      this.editPersonInformationDialog = false;
      this.deselectCurrentPerson();
      this.deselectCurrentRentalProduct();
    },
    searchAvailableProducts() {
      this.isSearchingForAnotherProduct = true;
      const areaId = this.selectedArea || this.defaultAreaId;
      this.loadAvailableRentalProducts({
        areaId,
        numberOfDays: this.selectedNumberOfDays,
        participantAge: this.selectedRental.participant.age,
        skiStartDayDate: this.selectedStartDate,
        targetCurrency: this.booking.currency,
      });
    },
    takeGrossNetPrice(availableSkiRentalProduct) {
      this.isProductChanged = true;
      this.selectedRentalProduct.totalGrossAfterDiscount
        = this.$options.filters.roundNumber(availableSkiRentalProduct.totalGrossAfterDiscount);
      this.selectedRentalProduct.totalNetAfterDiscount
        = this.$options.filters.roundNumber(availableSkiRentalProduct.totalNetAfterDiscount);
      this.selectedRentalProduct.price
        = this.$options.filters.roundNumber(availableSkiRentalProduct.priceGross);
      this.selectedRentalProduct.priceNet
        = this.$options.filters.roundNumber(availableSkiRentalProduct.priceNet);
      this.selectedRentalProduct.currencyNet = availableSkiRentalProduct.season.supplier.currency;
      if (availableSkiRentalProduct.discountGross) {
        this.selectedRentalProduct.discount
          = this.$options.filters.roundNumber(availableSkiRentalProduct.discountGross);
        this.selectedRentalProduct.discountNet
          = this.$options.filters.roundNumber(availableSkiRentalProduct.discountNet);
        this.selectedRentalProduct.discountBookFrom = availableSkiRentalProduct.discount.bookFrom;
        this.selectedRentalProduct.discountBookTo = availableSkiRentalProduct.discount.bookTo;
        this.selectedRentalProduct.discountPercentage
          = availableSkiRentalProduct.discount.percentage;
      } else {
        this.selectedRentalProduct.discount = null;
        this.selectedRentalProduct.discountNet = null;
        this.selectedRentalProduct.discountBookFrom = null;
        this.selectedRentalProduct.discountBookTo = null;
        this.selectedRentalProduct.discountPercentage = null;
      }
    },
    sendRequestEmail() {
      this.sendSkiRentalRequestEmail(this.booking.id);
    },
    previewRequestEmail() {
      return `${process.env.TWENTY_API_URL}booking/ski/rental/booking/${this.booking.id}/preview-supplier-request`;
    },
    onStartDateChange(startDate) {
      this.setChangeProductStartDate(startDate);
      this.searchAvailableProducts();
    },
    onNumberOfDaysChange(days) {
      this.setChangeProductNumberOfDays(days);
      this.searchAvailableProducts();
    },
    onAreaChange(areaId) {
      this.selectedArea = areaId;
      this.searchAvailableProducts();
    },
    onPersonCategoryChange(personCategoryId) {
      this.setChangeProductPersonCategory(personCategoryId);
    },
    onLevelTypeChange(levelTypeId) {
      this.setChangeProductLevelType(levelTypeId);
    },
    onSkiTypeChange(skiTypeId) {
      this.setChangeProductSkiType(skiTypeId);
    },
    onPackageTypeChange(packageTypeId) {
      this.setChangeProductPackageType(packageTypeId);
    },
    onPriceOverrideChange() {
      this.isPriceChanged = true;
    },
    onFilterAreaChange(areaId) {
      this.setArea(areaId);
    },
    onBookingParticipantChange(participantId) {
      this.setBookingParticipant(participantId);
    },
    assignParticipantToSkiRental(participantId, skiRentalId) {
      this.assignParticipant({ participantId, skiRentalId });
    },
  },
  computed: {
    ...mapGetters({
      rentalsWithProducts: 'skirental/getSkiRentalsWithProducts',
      totalPrice: 'skirental/getSkiRentalsTotalPrice',
      availableRentalProducts: 'skirental/getAvailableSkiRentalProducts',
      bookingParticipantsWhereRentalsExist: 'skirental/getBookingParticipantsWhereRentalsExist',
      participants: 'booking/getParticipantsWithFullName',
      defaultStartDate: 'booking/getMinimumArrivalDateFromBookingStays',
      availableDates: 'booking/getAvailableDates',
      availableRentalProductsPersonCategoryHeaders: 'skirental/getAvailableSkiRentalProductsPersonCategoryHeaders',
      availableSkiRentalProductUsedForChangeProduct: 'skirental/getAvailableSkiRentalProductUsedForChangeProduct',
      availableSkiTypes: 'skirental/getAvailableSkiTypesUsedForChangeProduct',
      availableLevelTypes: 'skirental/getAvailableLevelTypesUsedForChangeProduct',
      availablePackageTypes: 'skirental/getAvailablePackageTypesUsedForChangeProduct',
      twentyBookingCustomerUrl: 'booking/getTwentyBookingCustomerUrl',
      defaultAreaId: 'booking/getDefaultAreaId',
      areasWithAllOption: 'skirental/getBookingStayAreasWithAllOption',
    }),
    ...mapState({
      rentals: state => state.skirental.rentals.items,
      rentalsLoading: state => state.skirental.rentals.itemsLoading,
      rentalsError: state => state.skirental.rentals.itemsError,
      rentalsSaveLoading: state => state.skirental.rentals.saveLoading,
      rentalsSaveError: state => state.skirental.rentals.saveError,
      booking: state => state.booking.booking,
      bookingLoading: state => state.booking.booking.itemLoading,
      bookingError: state => state.booking.booking.itemError,
      bookingParticipantsLoading: state => state.booking.participants.itemsLoading,
      bookingParticipantsError: state => state.booking.participants.itemsError,
      user: state => state.app.auth.user,
      availableRentalProductsLoading: state => state.skirental.availableRentalProducts.itemsLoading,
      availableRentalProductsError: state => state.skirental.availableRentalProducts.itemsError,
      availableRentalProductsSaveLoading:
        state => state.skirental.availableRentalProducts.saveLoading,
      availableRentalProductsSaveError: state => state.skirental.availableRentalProducts.saveError,
      availableNumberOfDays: state => state.skirental.search.availableNumberOfDays,
      selectedBookingParticipantId:
        state => state.skirental.changeProduct.selectedBookingParticipantId,
      selectedPersonCategoryId: state => state.skirental.changeProduct.selectedPersonCategoryId,
      selectedStartDate: state => state.skirental.changeProduct.selectedStartDate,
      selectedNumberOfDays: state => state.skirental.changeProduct.selectedNumberOfDays,
      selectedLevelTypeId: state => state.skirental.changeProduct.selectedLevelTypeId,
      selectedSkiTypeId: state => state.skirental.changeProduct.selectedSkiTypeId,
      selectedPackageTypeId: state => state.skirental.changeProduct.selectedPackageTypeId,
      selectedSearchBookingParticipantId:
        state => state.skirental.search.selectedBookingParticipantId,
      selectedAreaId: state => state.skirental.search.selectedAreaId,
      areas: state => state.booking.areas.items,
    }),
    canChangeProduct() {
      return (this.isProductChanged || this.isPriceChanged)
      && (
        !this.isSearchingForAnotherProduct
        || (this.isSearchingForAnotherProduct
          && this.availableSkiRentalProductUsedForChangeProduct
        )
      )
      && this.selectedRentalProduct.price !== undefined
      && this.selectedRentalProduct.priceNet !== undefined
      && this.selectedPersonCategoryId
      && this.selectedStartDate
      && this.selectedNumberOfDays
      && this.selectedLevelTypeId
      && this.selectedSkiTypeId
      && this.selectedPackageTypeId;
    },
    loading() {
      return this.rentalsLoading
        || this.rentalsSaveLoading
        || this.bookingLoading
        || this.bookingParticipantsLoading;
    },
    errorOccurred() {
      return this.rentalsError || this.bookingError || this.bookingParticipantsError;
    },
  },
};
</script>

<template>
  <v-container v-if="booking && booking.id" class="pa-0 pt-3" grid-list-xl>
    <div class="text-center pa-1">
      <v-progress-linear v-show="loading" :indeterminate="true"/>
    </div>
    <v-card
      class="pa-3 mb-3 mx-auto"
      outlined
    >
      <v-layout class="pt-3 pl-3 pr-3">
        <v-flex class="ma-0 pa-0" sm5 xs12>
          <v-select
            @change="onBookingParticipantChange($event)"
            :value="selectedSearchBookingParticipantId"
            :items="bookingParticipantsWhereRentalsExist"
            :loading="bookingParticipantsLoading"
            :disabled="bookingParticipantsLoading || !participants.length"
            :label="$t('ski.rental.selects.booking-participant')"
            item-text="fullName"
            item-value="id"
          />
        </v-flex>
        <v-flex v-if="areas.length > 1" class="pl-2 ma-0 pa-0" sm2 xs12>
          <v-select
            @change="onFilterAreaChange($event)"
            :value="selectedAreaId"
            :items="areasWithAllOption"
            :label="$t('ski.rental.selects.area')"
            item-text="name"
            item-value="id"
          />
        </v-flex>
      </v-layout>
    </v-card>
    <v-alert
      v-show="!rentalsWithProducts.length
        && !this.bookingLoading
        && !this.bookingParticipantsLoading
      "
      color="warning"
      icon="info"
      outline
    >
      {{ $t('ski.rental.no-ski-rentals-exist-on-this-booking') }}
    </v-alert>
    <template v-for="rental in rentalsWithProducts">
      <div
        v-for="rentalProduct in rental.skiRentalProducts"
        :key="rentalProduct.id"
        class="pt-2 pb-2"
      >
        <v-layout class="pb-0 mb-0" row>
          <v-flex class="pb-0 mb-0" sm4 xs12>
            <h2 class="font-weight-bold">
              <div v-if="rental.participant">
                {{ rental.participant.firstName }}
                {{ rental.participant.lastName }}
              </div>
              <div v-else>
                Unknown
              </div>
              <template v-if="areas.length > 1">
                ({{ rental.area.name }})
              </template>
            </h2>
            <div v-if="rentalProduct.ageMismatch">
              <v-icon class="red--text" title="Missing height">warning</v-icon>
              <span class="red--text">{{ $t('ski.rental.age-mismatch') }}</span>
            </div>
          </v-flex>
          <v-flex class="pb-0 mb-0 text-md-center" sm2 xs3>
            <v-chip :color="rentalProduct.statusColor" text-color="white" disabled>
              {{ rentalProduct.statusName }}
              <v-icon right>{{ rentalProduct.statusIcon }}</v-icon>
            </v-chip>
          </v-flex>
          <v-flex class="pb-0 mb-0 no-wrap" sm2 xs12>
            <template v-if="rentalProduct.discount">
              <h2>
                {{ rentalProduct.totalGrossAfterDiscount }} {{ booking.currency }}
                <span class="blue-grey--text caption">
                  <template v-if="rentalProduct.cancellationFeeNet !== null">
                    / {{ rentalProduct.cancellationFeeNet }} {{ rentalProduct.currencyNet }}
                  </template>
                  <template v-else>
                    / {{ rentalProduct.totalNetAfterDiscount }} {{ rentalProduct.currencyNet }}
                  </template>
                </span>
                <v-chip color="green" text-color="white" disabled>
                  -{{ ((rentalProduct.discount / rentalProduct.price) * 100) | roundNumber }}%
                </v-chip>
              </h2>
            </template>
            <template v-else>
              <h2>
                {{ rentalProduct.price }} {{ booking.currency }}
                <span class="blue-grey--text body-2 caption">
                  / {{ rentalProduct.priceNet }} {{ rentalProduct.currencyNet }}
                </span>
              </h2>
            </template>
          </v-flex>
          <v-flex class="pb-0 mb-0 ml-auto text-md-right no-wrap" sm3 xs0>
            <v-btn
              @click.stop="openEditProductDialog(rental, rentalProduct)"
              :loading="loading"
              color="warning"
              dark
              small
            >
              {{ $t('inputs.actions.edit') }}
            </v-btn>
            <template v-if="['stopped', 'cancelled'].includes(rentalProduct.status)">
              <v-btn
                @click="reOpenProductDialog(rentalProduct.id)"
                :loading="loading"
                color="error"
                small
              >
                {{ $t('inputs.actions.reopen') }}
              </v-btn>
            </template>
            <template v-else>
              <v-btn
                v-if="rentalProduct.supplierRef"
                @click="stopProductDialog(rentalProduct.id)"
                :loading="loading"
                color="error"
                small
              >
                {{ $t('inputs.actions.stop') }}
              </v-btn>
              <v-btn
                v-else
                @click="deleteProductDialog(rentalProduct.id)"
                :loading="loading"
                color="error"
                small
              >
                {{ $t('inputs.actions.delete') }}
              </v-btn>
            </template>
          </v-flex>
        </v-layout>
        <v-layout class="no-wrap" row>
          <v-flex class="font-weight-bold pt-0 pb-0 mb-0" sm3 xs12>
            {{ rentalProduct.personCategoryName }}
          </v-flex>
          <v-flex class="font-weight-bold pt-0 pb-0 mb-0" sm3 xs12>
            {{ rentalProduct.skiTypeName }} ({{ rentalProduct.packageName }})
          </v-flex>
          <v-flex v-if="rentalProduct.discount" class="pt-0 pb-0 mb-0" sm6 xs12>
            <p class="text-line-through pb-0 mb-0 pa-0">
              {{ rentalProduct.price }} {{ booking.currency }}
              <span class="blue-grey--text body-2 caption">
                  / {{ rentalProduct.priceNet }} {{ rentalProduct.currencyNet }}
              </span>
            </p>
          </v-flex>
        </v-layout>
        <v-layout class="no-wrap" row>
          <v-flex class="pb-0 mb-0" sm3 xs12>
            {{ $t('ski.rental.level') }}:
            <span class="font-weight-bold">{{ rentalProduct.levelName }}</span>
          </v-flex>
          <v-flex class="pb-0 mb-0" sm3 xs12>
            {{ $t('ski.rental.first-ski-day') }}:
            <span class="font-weight-bold">{{ rentalProduct.firstSkiDay | formatDate }}</span>
          </v-flex>
          <v-flex class="pb-0 mb-0" sm3 xs12>
            {{ $t('ski.rental.number-of-days') }}:
            <span class="font-weight-bold">
              {{ rentalProduct.numberOfDays }} {{ $t('ski.rental.days') }}
            </span>
          </v-flex>
          <v-flex class="py-0 my-0 text-md-right" sm3 xs12>
            <v-text-field
              @blur="updateSupplierRef(rentalProduct)"
              v-model="supplierRefs[rentalProduct.id]"
              :disabled="rentalsSaveLoading"
              :label="$t('ski.rental.supplier-ref')"
              :success="!!rentalProduct.supplierRef"
              :error="!rentalProduct.supplierRef"
              :append-outer-icon="rentalProduct.supplierRef ? 'check_circle' : 'warning'"
              type="text"
              class="py-0 my-0"
              hide-details
            />
          </v-flex>
        </v-layout>
        <v-layout class="no-wrap pb-2" row>
          <v-flex v-if="rental.participant" class="pb-0 mb-0" sm3 xs12>
            {{ $t('ski.rental.birthday') }}:
            <span class="font-weight-bold">{{ rental.participant.birthday | formatDate }}</span>
            <v-icon v-if="rental.participant.validBirthday" class="green--text">
              check_circle
            </v-icon>
            <v-icon v-else class="red--text" title="Birthday not verified">warning</v-icon>
          </v-flex>
          <v-flex class="pb-0 mb-0" sm2 xs12>
            {{ $t('ski.rental.height') }}:
            <template v-if="rental.height > 0">
              <span class="font-weight-bold">{{ rental.height }} cm</span>
              <v-icon class="green--text">check_circle</v-icon>
            </template>
            <template v-else>
              <span class="font-weight-bold red--text">{{ $t('ski.rental.missing') }}</span>
              <v-icon class="red--text" title="Missing height">warning</v-icon>
            </template>
          </v-flex>
          <v-flex class="pb-0 mb-0" sm2 xs12>
            {{ $t('ski.rental.shoe-size') }}:
            <template v-if="rental.shoeSize > 0">
              <span class="font-weight-bold">{{ rental.shoeSize }}</span>
              <v-icon class="green--text">check_circle</v-icon>
            </template>
            <template v-else>
              <span class="font-weight-bold red--text">{{ $t('ski.rental.missing') }}</span>
              <v-icon class="red--text" title="Missing shoe size">warning</v-icon>
            </template>
          </v-flex>
          <v-flex class="pb-0 mb-0" sm2 xs12>
            {{ $t('ski.rental.weight') }}:
            <template v-if="rental.weight > 0">
              <span class="font-weight-bold">{{ rental.weight }} kg</span>
              <v-icon class="green--text">check_circle</v-icon>
            </template>
            <template v-else>
              <span class="font-weight-bold red--text">{{ $t('ski.rental.missing') }}</span>
              <v-icon class="red--text" title="Missing weight">warning</v-icon>
            </template>
          </v-flex>
          <v-flex class="pb-0 mb-0 text-md-right" sm3 xs12>
            <v-btn
              @click.stop="openEditPersonInformationDialog(rental, rentalProduct)"
              :loading="loading"
              :disabled="!rental.participant"
              :color="rental.isAllPersonInformationFilledOut ? 'success' : 'error'"
              small
            >
              {{ $t('ski.rental.edit-person') }}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-divider></v-divider>
        <v-flex v-if="!rental.participant && rentalProduct.cancellationFeeNet === null" xs3>
          <v-select
            @change="assignParticipantToSkiRental($event, rental.id)"
            :items="participants"
            :label="'Select participant'"
            item-text="fullName"
            item-value="id"
          />
        </v-flex>
      </div>
    </template>
    <v-layout
      v-show="rentalsWithProducts.length
        && !this.bookingLoading
        && !this.bookingParticipantsLoading
      "
      class="no-wrap pt-3"
      row
    >
      <v-flex class="ma-0 pa-0 text-md-right" sm8 xs12>
        <a
          :href="previewRequestEmail()"
          target="_blank"
          class="v-btn theme--dark success"
        >
          Preview request
        </a>
        <v-btn
          @click="sendRequestEmail"
          :loading="loading"
          color="success"
          dark
        >
          {{ $t('ski.rental.send-request-email') }}
        </v-btn>
      </v-flex>
      <v-flex class="ma-0 pa-0 text-md-center" sm4 xs4>
        <h1 class="text-uppercase">
          {{ $t('ski.rental.total') }} {{ totalPrice.totalGross }} {{ booking.currency }}
          <span class="blue-grey--text caption">
            / {{ totalPrice.totalNet }}
          </span>
        </h1>
      </v-flex>
    </v-layout>
    <v-dialog
      v-if="selectedRental && selectedRentalProduct"
      v-model="editProductDialog"
      persistent
      max-width="800px"
    >
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ selectedRental.participant ? $t('ski.rental.products-information-for', {
              first: this.selectedRental.participant.firstName,
              last: this.selectedRental.participant.lastName
            }) : 'Unknown participant' }}
            <span class="body-2">
              {{ selectedRentalProduct.skiTypeName }}
              ({{ selectedRentalProduct.packageName }}) -
              {{ selectedRentalProduct.personCategoryName }}
            </span>
          </span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-3 mt-0">
          <v-container class="pt-0 mt-0" grid-list-md>
            <v-alert
              v-if="rentalsSaveError"
              :value="rentalsSaveError"
              color="error"
              icon="warning"
              outline
            >
              <span v-html="rentalsSaveError"></span>
            </v-alert>
            <v-layout wrap>
              <v-flex class="pt-0 mt-0" xs12 sm12 md12>
                <h2>
                  {{ $t('ski.rental.override-prices') }}
                  <v-chip v-show="isPriceChanged" color="red" text-color="white" disabled>
                    {{ $t('ski.rental.price-modified-and-not-saved-yet') }}
                  </v-chip>
                </h2>
              </v-flex>
              <v-flex xs12 sm3 md3>
                <v-text-field
                  v-model="selectedRentalProduct.totalGrossAfterDiscount"
                  @input="onPriceOverrideChange"
                  :suffix="booking.currency"
                  :disabled="
                    loading
                    || bookingParticipantsLoading
                    || availableRentalProductsLoading
                  "
                  :label="$t('ski.rental.price-gross')"
                  required
                />
              </v-flex>
              <v-flex xs12 sm3 md3>
                <v-text-field
                  v-model="selectedRentalProduct.totalNetAfterDiscount"
                  @input="onPriceOverrideChange"
                  :suffix="selectedRentalProduct.currencyNet"
                  :disabled="
                    loading
                    || selectedRentalProduct.cancellationFeeNet !== null
                    || bookingParticipantsLoading
                    || availableRentalProductsLoading
                  "
                  :label="$t('ski.rental.price-net')"
                  required
                />
              </v-flex>
              <v-flex xs12 sm4 md4>
                <v-text-field
                  v-model="selectedRentalProduct.cancellationFeeNet"
                  @input="onPriceOverrideChange"
                  :suffix="selectedRentalProduct.currencyNet"
                  :disabled="loading || (selectedRentalProduct.cancellationFeeNet === null)"
                  :label="$t('ski.pass.cancellation-fee-net')"
                  placeholder="0"
                />
              </v-flex>
              <v-flex xs12 sm12 md12>
                <h2>
                  {{ $t('ski.rental.change-product') }}
                  <v-chip v-show="isProductChanged" color="red" text-color="white" disabled>
                    {{ $t('ski.rental.product-modified-and-not-saved-yet') }}
                  </v-chip>
                </h2>
              </v-flex>
              <v-flex class="text-xs-right" xs12 sm3>
                <v-select
                  @change="onStartDateChange($event)"
                  :value="selectedRentalProduct.firstSkiDay"
                  :items="availableDates"
                  :loading="bookingParticipantsLoading || availableRentalProductsLoading"
                  :disabled="bookingParticipantsLoading || availableRentalProductsLoading"
                  :label="$t('ski.rental.selects.start-date')"
                  item-text="name"
                  item-value="id"
                />
              </v-flex>
              <v-flex xs12 sm3>
                <v-select
                  @change="onNumberOfDaysChange($event)"
                  :value="selectedRentalProduct.numberOfDays"
                  :items="availableNumberOfDays"
                  :loading="bookingParticipantsLoading || availableRentalProductsLoading"
                  :disabled="bookingParticipantsLoading || availableRentalProductsLoading"
                  :label="$t('ski.rental.selects.number-of-days')"
                />
              </v-flex>
              <v-flex v-if="areas.length > 1" sm2 xs12>
                <v-select
                  @change="onAreaChange($event)"
                  :value="defaultAreaId || null"
                  :items="areas"
                  :loading="bookingParticipantsLoading || availableRentalProductsLoading"
                  :disabled="bookingParticipantsLoading || availableRentalProductsLoading"
                  :label="$t('ski.rental.selects.area')"
                  item-text="name"
                  item-value="id"
                />
              </v-flex>
              <v-flex xs12 sm4>
                <v-btn
                  @click="searchAvailableProducts"
                  :loading="availableRentalProductsLoading"
                  color="warning"
                  dark
                >
                  {{ $t('ski.rental.search-product') }}
                </v-btn>
              </v-flex>
              <template
                v-if="isSearchingForAnotherProduct && Object.keys(availableRentalProducts).length"
              >
                <v-flex xs12 sm3>
                  <v-select
                    @change="onPersonCategoryChange($event)"
                    :items="availableRentalProductsPersonCategoryHeaders"
                    :disabled="loading || availableRentalProductsLoading"
                    :value="selectedRentalProduct.productsSkiRentalPersonCategoryId"
                    :label="$t('ski.rental.selects.person-category')"
                    item-text="name"
                    item-value="id"
                    required
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm3>
                  <v-select
                    @change="onSkiTypeChange($event)"
                    :value="selectedRentalProduct.productsSkiRentalSkiTypeId"
                    :items="availableSkiTypes"
                    :disabled="
                      !availableSkiTypes.length
                      || loading
                      || availableRentalProductsLoading
                    "
                    :label="$t('ski.rental.selects.ski-type')"
                    item-text="name"
                    item-value="id"
                    required
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm3>
                  <v-select
                    @change="onLevelTypeChange($event)"
                    :value="selectedRentalProduct.productsSkiRentalLevelId"
                    :items="availableLevelTypes"
                    :disabled="
                      !availableSkiTypes.length
                      || loading
                      || availableRentalProductsLoading
                    "
                    :label="$t('ski.rental.selects.level-type')"
                    item-text="name"
                    item-value="id"
                    required
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm3>
                  <v-select
                    @change="onPackageTypeChange($event)"
                    :value="selectedRentalProduct.productsSkiRentalPackageId"
                    :items="availablePackageTypes"
                    :disabled="
                      !availableSkiTypes.length
                      || loading
                      || availableRentalProductsLoading
                    "
                    :label="$t('ski.rental.selects.ski-package')"
                    item-text="name"
                    item-value="id"
                    required
                  ></v-select>
                </v-flex>
              </template>
              <template v-if="isSearchingForAnotherProduct">
                <template v-if="availableSkiRentalProductUsedForChangeProduct">
                  <v-flex xs12 sm12>
                    <h3>{{ $t('ski.rental.available-product-and-price-found') }}</h3>
                  </v-flex>
                  <template v-if="availableSkiRentalProductUsedForChangeProduct.discountGross">
                    <v-flex xs12 sm3>
                      <p class="font-weight-bold pb-0 mb-0">
                        {{ $t('ski.rental.price-before-discount') }}:
                      </p>
                    </v-flex>
                    <v-flex xs12 sm9>
                      <p class="font-weight-bold pb-0 mb-0">
                        {{ availableSkiRentalProductUsedForChangeProduct.priceGross }}
                        {{ booking.currency }}
                        <span class="blue-grey--text body-2 caption">
                          / {{ availableSkiRentalProductUsedForChangeProduct.priceNet }}
                          {{
                            availableSkiRentalProductUsedForChangeProduct.season.supplier.currency
                          }}
                        </span>
                      </p>
                    </v-flex>
                    <v-flex xs12 sm3>
                      <p class="font-weight-bold pb-0 mb-0">
                        {{ $t('ski.rental.discount') }}:
                      </p>
                    </v-flex>
                    <v-flex xs12 sm9>
                      <p class="font-weight-bold pb-0 mb-0">
                        -{{ availableSkiRentalProductUsedForChangeProduct.discountGross }}
                        {{ booking.currency }}
                        <span class="blue-grey--text body-2 caption">
                          / -{{ availableSkiRentalProductUsedForChangeProduct.discountNet }}
                          {{
                            availableSkiRentalProductUsedForChangeProduct.season.supplier.currency
                          }}
                        </span>
                      </p>
                    </v-flex>
                    <v-flex xs12 sm3>
                      <p class="font-weight-bold pb-0 mb-0">
                        {{ $t('ski.rental.total-after-discount') }}:
                      </p>
                    </v-flex>
                    <v-flex xs12 sm9>
                      <p class="font-weight-bold pb-0 mb-0">
                        {{ availableSkiRentalProductUsedForChangeProduct.totalGrossAfterDiscount }}
                        {{ booking.currency }}
                        <span class="blue-grey--text body-2 caption">
                          /
                          {{ availableSkiRentalProductUsedForChangeProduct.totalNetAfterDiscount }}
                          {{
                            availableSkiRentalProductUsedForChangeProduct.season.supplier.currency
                          }}
                        </span>
                      </p>
                    </v-flex>
                  </template>
                  <template v-else>
                    <v-flex xs12 sm12>
                      <p class="font-weight-bold pb-0 mb-0">
                        {{ $t('ski.rental.total') }}:
                        {{ availableSkiRentalProductUsedForChangeProduct.priceGross }}
                        {{ booking.currency }}
                        <span class="blue-grey--text body-2 caption">
                          / {{ availableSkiRentalProductUsedForChangeProduct.priceNet }}
                          {{
                            availableSkiRentalProductUsedForChangeProduct.season.supplier.currency
                          }}
                        </span>
                      </p>
                    </v-flex>
                  </template>
                  <v-flex xs12 sm12>
                    <v-btn
                      @click="takeGrossNetPrice(availableSkiRentalProductUsedForChangeProduct)"
                      :loading="availableRentalProductsLoading"
                      color="warning"
                      dark
                    >
                      {{ $t('ski.rental.take-gross-net-price') }}
                    </v-btn>
                  </v-flex>
                </template>
                <template v-else>
                  <h3 v-show="!availableRentalProductsLoading" class="red--text">
                    {{ $t('ski.rental.no-available-price-found') }}
                  </h3>
                </template>
              </template>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="closeProductDialog"
            :loading="loading"
            color="red darken-1"
            flat
          >
            {{ $t('dialog.actions.close') }}
          </v-btn>
          <v-btn
            @click="saveProductDialog"
            :disabled="!canChangeProduct"
            :loading="loading || availableRentalProductsSaveLoading"
            color="green darken-1"
            flat
          >
            {{ $t('dialog.actions.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="selectedRental && selectedRentalProduct"
      v-model="editPersonInformationDialog"
      persistent max-width="700px"
    >
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ selectedRental.participant ? $t('ski.rental.persons-information-for', {
              first: this.selectedRental.participant.firstName,
              last: this.selectedRental.participant.lastName
            }) : 'Unknown participant' }}
          </span>
          <h3>
            {{ selectedRentalProduct.skiTypeName }}
            ({{ selectedRentalProduct.packageName }}) -
            {{ selectedRentalProduct.personCategoryName }}
          </h3>
        </v-card-title>
        <v-card-text class="ma-0 pa-0">
          <v-container grid-list-md>
            <v-alert
              v-if="rentalsSaveError"
              :value="rentalsSaveError"
              color="error"
              icon="warning"
              outline
            >
              <span v-html="rentalsSaveError"></span>
            </v-alert>
            <v-layout wrap>
              <v-flex v-if="selectedRental.participant" class="ma-0 pa-0 pb-4" xs12 sm12 md12>
                {{ $t('ski.rental.birthday') }}:
                <span
                  v-if="selectedRental.participant.validBirthday"
                  class="font-weight-bold"
                >
                  {{ this.selectedRental.participant.birthday | formatDate }}
                  ({{ this.selectedRental.participant.age }}
                  {{ $t('ski.rental.years-old') }})
                  <v-icon class="green--text">
                    check_circle
                  </v-icon>
                </span>
                <span v-else class="font-weight-bold">
                  {{ this.selectedRental.participant.age }} {{ $t('ski.rental.years-old') }}
                  <v-icon class="red--text">warning</v-icon>
                  <span class="font-weight-bold red--text">
                    <a :href="twentyBookingCustomerUrl" target="_blank" class="font-weight-bold">
                      {{ $t('ski.rental.birthday-not-verified') }}
                    </a>
                  </span>
                </span>
              </v-flex>
              <v-flex xs12 sm3 md3>
                <v-text-field
                  v-model="selectedRental.height"
                  :value="selectedRental.height"
                  :label="$t('ski.rental.height-cm')"
                  :success="selectedRental.height > 0"
                  :error="!selectedRental.height"
                  type="number"
                >
                  <template slot="append-outer">
                    <v-icon v-if="selectedRental.height" class="green--text">check_circle</v-icon>
                    <v-icon v-else class="red--text">warning</v-icon>
                  </template>
                </v-text-field>
              </v-flex>
              <v-flex xs12 sm3 md3 offset-sm1>
                <v-text-field
                  v-model="selectedRental.shoeSize"
                  :value="selectedRental.shoeSize"
                  :label="$t('ski.rental.shoe-size')"
                  :success="selectedRental.shoeSize > 0"
                  :error="!selectedRental.shoeSize"
                  type="number"
                >
                  <template slot="append-outer">
                    <v-icon v-if="selectedRental.shoeSize" class="green--text">check_circle</v-icon>
                    <v-icon v-else class="red--text">warning</v-icon>
                  </template>
                </v-text-field>
              </v-flex>
              <v-flex xs12 sm3 md3 offset-sm1>
                <v-text-field
                  v-model="selectedRental.weight"
                  :value="selectedRental.weight"
                  :label="$t('ski.rental.weight-kg')"
                  :success="selectedRental.weight > 0"
                  :error="!selectedRental.weight"
                  type="number"
                >
                  <template slot="append-outer">
                    <v-icon v-if="selectedRental.weight" class="green--text">check_circle</v-icon>
                    <v-icon v-else class="red--text">warning</v-icon>
                  </template>
                </v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="closeEditPersonInformationDialog"
            :loading="loading"
            color="red"
            flat
          >
            {{ $t('inputs.actions.cancel') }}
          </v-btn>
          <v-btn
            @click="saveEditPersonInformationDialog"
            :loading="loading"
            color="green darken-1"
            flat
          >
            {{ $t('inputs.actions.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>



// WEBPACK FOOTER //
// src/pages/booking/ski/rental/SkiRentalOverview.vue